import React from "react";
import { Card, CardContent, CardFooter } from "@components/common/card";
import axios from "axios";
import {
  GET_ORGANIZATION,
  SERVER_URL,
  USER_ORGANIZATION_UPDATE_ENDPOINT,
} from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import useTranslator from "@hooks/use-translator";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  LOGIN_COMPLETE_PAGE_ROUTE,
  USERS_PAGE_ROUTE,
} from "@pma/utils/page-routes";
import { Button } from "@components/common/button";
import { ArrowLeftRight } from "lucide-react";
import { toast } from "@hooks/use-toast";
import { DictType } from "@pma/types";
import { useModal } from "@stores/modal-store";
import { useAuth } from "@stores/auth-store";
import { cn } from "@pma/utils/helpers";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  id: string;
  isCurrent?: boolean;
}

const WorkspaceDetails = ({ id, isCurrent, className }: Props) => {
  const { translate } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const modal = useModal();

  const { signIn } = useAuth();

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const workSpaceDetails = async (id:string) => {
    
    const response = await axios.get(SERVER_URL + GET_ORGANIZATION + `/${id}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return response?.data;
  };

  const { data: organization } = useQuery({
    queryKey: ["workspace-details", id],
    queryFn:()=> workSpaceDetails(id),
    
  });

  const switchWorkspace = async (id:string) => {
    const response = await axios.post(
      SERVER_URL + USER_ORGANIZATION_UPDATE_ENDPOINT,
      {
        last_organization_id: id,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const switchWorkspaceMutation = useMutation({
    mutationKey: ["switch-workspace", id],
    mutationFn:(id:string)=> switchWorkspace(id),
    onSuccess: (data: DictType) => {
      signIn({ ...data });
      toast({
        title: translate("Success"),
        description: translate("Switching workspace"),
        variant: "success",
      });

      // Close modal
      modal.onClose();
      modal.setContext(undefined);

      queryClient.clear();

      navigate(LOGIN_COMPLETE_PAGE_ROUTE, { state: { from: location.pathname } });
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage ||
          translate("Unable to switch workspace at this time"),
        variant: "error",
      });
    },
  });
  return (
    <Card className={cn(className, "min-h-[150px] px-0")}>
      <CardContent className="flex items-center justify-center flex-col p-0 mb-2">
        <h3 className="text-lg font-semibold">
          {organization?.owner?.organization_name || "---"}
        </h3>
        <img
          src={
            organization?.owner?.company_logo?.url ||
            `${process.env.PUBLIC_URL}/group-232.svg`
          }
          alt="Organization logo"
          className=" rounded-full h-[60px] w-[60px] bg-primary"
        />
      </CardContent>
      <CardFooter className="text-xs flex flex-wrap items-center gap-1 justify-between w-full ">
        {isCurrent && (
          <div className="flex items-center gap-1 mt-4 sm:m-auto">
            <span>
              {translate("Owner")}: {organization?.owner?.name || "---"}
            </span>
            <Link to={USERS_PAGE_ROUTE + `/${organization?.owner_id}`}>
              <img
                alt=""
                src={
                  organization?.owner?.user_picture?.url
                    ? organization?.owner?.user_picture.url
                    : `${process.env.PUBLIC_URL}/ionaddoutline@2x.png`
                }
                className="h-6 w-6 rounded-full"
              />
            </Link>
          </div>
        )}
        {!isCurrent && (
          <div className="flex justify-end w-full mt-2">
            <Button
              size={"sm"}
              variant={"secondary"}
              className="md:w-[100px] h-7 text-xs"
              loading={switchWorkspaceMutation.isPending}
              disabled={
                switchWorkspaceMutation.isPending ||
                switchWorkspaceMutation.isSuccess
              }
              onClick={() =>
                switchWorkspaceMutation.mutate(id)
              }
              leftSection={<ArrowLeftRight size={14} />}
            >
              <span className="hidden md:flex">{translate("Switch")} </span>{" "}
            </Button>
          </div>
        )}
      </CardFooter>
    </Card>
  );
};

export default WorkspaceDetails;
